import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { useWa } from "@/lib/content";
import { scrollToId } from "@/lib/smooth";
import { EASE, WhatsIcon } from "./Primitives";
import { LogoSvg } from "./LogoSvg";

import { useContent, rich } from "@/lib/content";

const Line = ({ children, i }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay: 0.25 + i * 0.14, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const TiltFrame = ({ photo, caption }) => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), { stiffness: 120, damping: 18 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => {
    mx.set(0);
    my.set(0);
  };
  return (
    <div className="relative [perspective:1200px]" onMouseMove={onMove} onMouseLeave={reset}>
      <motion.div
        initial={{ clipPath: "inset(100% 0 0 0 round 999px 999px 24px 24px)" }}
        animate={{ clipPath: "inset(0% 0 0 0 round 999px 999px 24px 24px)" }}
        transition={{ duration: 1.4, delay: 0.5, ease: EASE }}
        style={{ rotateX: rx, rotateY: ry }}
        className="relative aspect-[2/3] w-full overflow-hidden rounded-t-full rounded-b-3xl border border-gold/30 shadow-[0_40px_120px_-30px_rgba(197,160,89,0.35)]"
      >
        <motion.img
          initial={{ scale: 1.15 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2.2, delay: 0.5, ease: EASE }}
          src={photo}
          alt="Repostería alemana Café Amadeus"
          className="h-full w-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-espresso/90 via-espresso/10 to-transparent" />
        <p className="absolute bottom-6 left-6 right-6"><span className="inline-block rounded-full border border-gold/30 bg-espresso/80 px-4 py-1.5 font-display text-lg italic text-cream backdrop-blur-md">{caption}</span></p>
      </motion.div>
      <div data-testid="hero-seal" className="absolute -left-2 -top-5 z-10 h-20 w-20 rounded-full border border-gold/50 bg-espresso/90 shadow-[0_10px_30px_rgba(0,0,0,0.5)] backdrop-blur-md sm:-left-6 sm:-top-6 sm:h-28 sm:w-28 md:-left-10 md:-top-8 md:h-32 md:w-32">
        <svg viewBox="0 0 100 100" className="h-full w-full animate-spin-slow text-gold-light">
          <defs>
            <path id="circ" d="M50,50 m-38,0 a38,38 0 1,1 76,0 a38,38 0 1,1 -76,0" />
          </defs>
          <text fontSize="9.5" letterSpacing="3" fill="currentColor" className="font-sans uppercase" transform="rotate(152 50 50)">
            <textPath href="#circ" textLength="236" lengthAdjust="spacing">Seit 1986 · Querétaro · Café Amadeus ·</textPath>
          </text>
        </svg>
        <span className="absolute inset-0 flex items-center justify-center font-display text-base text-cream sm:text-xl md:text-2xl">1986</span>
      </div>
    </div>
  );
};

export const Hero = () => {
  const { TEXTS: T } = useContent();
  const waLink = useWa();
  const LINES = [T.hero_l1, T.hero_l2, T.hero_l3].filter(Boolean).map(rich);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const wordX = useTransform(scrollYProgress, [0, 1], ["0%", "-25%"]);

  return (
    <section id="inicio" ref={ref} data-testid="hero-section" className="relative min-h-[100svh] overflow-hidden pt-28 md:pt-32">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10">
        <img src="/img/nosotros1.jpg" alt="" className="h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_40%,rgba(197,160,89,0.18),transparent_60%)]" />
        <div className="absolute inset-0 bg-gradient-to-b from-espresso/60 via-espresso/80 to-espresso" />
      </motion.div>

      <motion.div
        style={{ x: wordX }}
        aria-hidden="true"
        data-testid="hero-bg-logo"
        className="pointer-events-none absolute bottom-6 left-0 right-0 select-none px-[2vw] text-transparent [&_path]:[stroke:rgba(197,160,89,0.22)] [&_path]:[stroke-width:0.6] [&_path]:[vector-effect:non-scaling-stroke]"
      >
        <LogoSvg part="cafe" className="block h-auto w-[52vw]" />
        <LogoSvg part="amadeus" className="mt-[1vw] block h-auto w-[96vw]" />
      </motion.div>

      <div className="relative mx-auto grid max-w-[1400px] items-center gap-14 px-5 pb-24 md:px-10 lg:grid-cols-12 lg:gap-10">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="mb-8 flex items-center gap-4 font-sans text-xs uppercase tracking-[0.3em] text-gold"
          >
            <span className="h-px w-10 bg-gold" /> {T.hero_eyebrow}
          </motion.p>
          <h1 data-testid="hero-title" className="font-display text-[2.6rem] leading-[0.95] text-cream sm:text-6xl md:text-7xl lg:text-[4.9rem] xl:text-[7.2rem]">
            {LINES.map((l, i) => (
              <Line key={i} i={i}>{l}</Line>
            ))}
          </h1>
          <motion.p
            data-testid="hero-subtitle"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
            className="mt-6 max-w-xl font-sans text-base leading-relaxed text-sand"
          >
            {T.hero_sub}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05, ease: EASE }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <button
              data-testid="hero-menu-cta"
              onClick={() => scrollToId("menu")}
              className="group inline-flex items-center gap-3 rounded-full bg-gold px-7 py-4 font-sans text-sm font-semibold text-espresso transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-gold-light"
            >
              {T.btn_hero_menu} <ArrowDown size={16} className="transition-transform group-hover:translate-y-0.5" />
            </button>
            <button
              data-testid="hero-pasteleria-cta"
              onClick={() => scrollToId("pasteleria")}
              className="inline-flex items-center gap-3 rounded-full border border-cream/30 px-7 py-4 font-sans text-sm text-cream transition-colors hover:border-gold hover:text-gold"
            >
              {T.btn_hero_pas}
            </button>
            <a
              data-testid="hero-whatsapp-cta"
              href={waLink()}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-2 py-4 font-sans text-sm text-sand transition-colors hover:text-[#25D366]"
            >
              <WhatsIcon className="h-4 w-4" /> {T.btn_hero_wa}
            </a>
          </motion.div>
        </div>
        <div className="mx-auto w-full max-w-[280px] sm:max-w-xs md:max-w-sm lg:col-span-5 lg:max-w-[440px] lg:pl-8">
          <TiltFrame photo={T.hero_photo} caption={T.hero_frame} />
        </div>
      </div>
    </section>
  );
};
